import { type ComputedRef, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGeneratorStore } from '@/stores/GeneratorStore'
import type { GenerationData, TweakData } from '@/types'

export type TagInjectionMode = 'generator' | 'tweak'

export interface TagInjectionComposable {
	// State
	current: ComputedRef<GenerationData | TweakData>
	computedTokens: ComputedRef<string[]>

	// Methods
	tokenizer: (prompt: string) => string[]
	isInjected: (tag: string) => boolean
	injectTag: (tag: string) => void
	injectMultipleTags: (tags: string[]) => void
	removeTag: (tag: string) => void
	addTag: (tag: string) => void
	getInjectedCount: (tags: string[]) => number
	replacePrompt: (newPrompt: string) => void
}

export function useTagInjection(mode: TagInjectionMode = 'generator'): TagInjectionComposable {
	const genStore = useGeneratorStore()
	const { generatorPrompt, tweakPrompt } = storeToRefs(genStore)

	const current = computed((): GenerationData | TweakData => {
		return mode === 'generator' ? generatorPrompt.value : tweakPrompt.value
	})

	function tokenizer(prompt: string): string[] {
		return prompt
			.split(',')
			.map((s: string) => s.trim())
			.filter((s: string) => s.length > 0)
	}

	const computedTokens: ComputedRef<string[]> = computed(() => tokenizer(current.value.prompt))

	function isInjected(tag: string): boolean {
		return computedTokens.value.includes(tag)
	}

	function injectTag(tag: string): void {
		const tokens: string[] = [...computedTokens.value] // Create a copy to avoid mutation
		if (isInjected(tag)) {
			const index: number = tokens.indexOf(tag)
			if (index !== -1) {
				tokens.splice(index, 1)
			}
		}
		else {
			tokens.push(tag)
		}
		const newPrompt: string = tokens.join(', ')
		current.value.prompt = newPrompt
	}

	function injectMultipleTags(tags: string[]): void {
		tags.forEach((tag: string) => injectTag(tag))
	}

	function removeTag(tag: string): void {
		if (isInjected(tag)) {
			const tokens: string[] = [...computedTokens.value]
			const index: number = tokens.indexOf(tag)
			if (index !== -1) {
				tokens.splice(index, 1)
				const newPrompt: string = tokens.join(', ')
				current.value.prompt = newPrompt
			}
		}
	}

	function addTag(tag: string): void {
		if (!isInjected(tag)) {
			const tokens: string[] = [...computedTokens.value]
			tokens.push(tag)
			const newPrompt: string = tokens.join(', ')
			current.value.prompt = newPrompt
		}
	}

	function getInjectedCount(tags: string[]): number {
		return Array.isArray(tags)
			? tags.filter((tag: string) => isInjected(tag)).length
			: 0
	}

	function replacePrompt(newPrompt: string): void {
		current.value.prompt = newPrompt
	}

	return {
		// State
		current,
		computedTokens,

		// Methods
		tokenizer,
		isInjected,
		injectTag,
		injectMultipleTags,
		removeTag,
		addTag,
		getInjectedCount,
		replacePrompt,
	}
}
